import { CtaBand } from "@/components/cms/CtaBand";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { PillarCard } from "@/components/ui/Cards";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { usePillars } from "@/content-context";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

/**
 * Our Work.
 *
 * The four programme cards come from the Programmes collection; each links to
 * that programme's own page, which is edited under Pages → Our Work. "How
 * programmes are run" and "Individual projects" are ordinary sections and render
 * through the generic renderer.
 */

export const loader = pageLoader("our-work");

export const meta = () =>
  buildMetadata({
    title: "Our work",
    description:
      "The four HISCUS programme pillars: women and girls, children and education, health and resilience, culture and dialogue, plus the projects beneath them.",
    path: "/our-work",
  });

function PillarsSection({ section }: SectionProps) {
  const pillars = usePillars();
  return (
    <Section>
      {section.title ? <SectionHeading eyebrow={section.eyebrow} title={section.title} lead={section.lead} /> : null}
      <RevealGroup as="ul" className={`${section.title ? "mt-10 " : ""}grid gap-6 lg:grid-cols-2`}>
        {pillars.map((pillar) => (
          <Reveal as="li" key={pillar.slug}>
            <PillarCard pillar={pillar} />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function ClosingSection({ section }: SectionProps) {
  return <CtaBand section={section} maxButtons={1} />;
}

export const ourWorkSections: SectionRenderers = {
  "our-work.pillars": PillarsSection,
  "our-work.cta": ClosingSection,
};

export function OurWorkPage() {
  return (
    <>
      <PageHeader
        eyebrow="Programmes"
        title="Four pillars, and the projects underneath them"
        lead="HISCUS groups its work into four public pillars. Every documented activity sits beneath one of them."
        trail={[
          { name: "Home", href: "/" },
          { name: "Our Work", href: "/our-work" },
        ]}
      />
      <PageSections renderers={ourWorkSections} />
    </>
  );
}
